<template>
    <q-btn
        :outline="outline"
        :color="color"
        class="filter-panel text-capitalize bg-white text-weight-medium ellipsis"
        :class="
            $q.screen.lt.sm
                ? 'filter-panel-mobile'
                : $q.screen.lt.md
                  ? 'filter-panel-tablet'
                  : ''
        "
        :icon="icon"
        :label="label"
        :align="alignLabel"
    >
        <q-menu :offset="[0, 10]">
            <div
                :style="searchOption ? 'width: 350px' : ''"
                class="q-pa-lg relative-position"
            >
                <label class="text-subtitle1 text-weight-bold">
                    {{ dropdownMenuLabel ? dropdownMenuLabel : label }}
                </label>
                <!-- search option -->
                <q-input
                    v-if="searchOption"
                    ref="filterRef"
                    v-model="filter"
                    label="Search"
                    outlined
                    class="q-mt-sm"
                    dense
                    :style="{ maxWidth: '350px' }"
                    color="accent"
                >
                    <template v-slot:append>
                        <q-icon
                            v-if="filter !== ''"
                            name="clear"
                            class="cursor-pointer"
                            @click="resetFilter"
                        ></q-icon>
                    </template>
                </q-input>

                <q-tree
                    :nodes="options"
                    node-key="value"
                    tick-strategy="leaf"
                    v-model:ticked="activeOptions"
                    :filter="filter"
                    v-model:selected="currentSelection"
                    selected-color="black"
                    :duration="20"
                    icon="add"
                    class="q-my-sm"
                    color="accent"
                    :no-nodes-label="$t('No available options')"
                >
                    <template v-slot:default-header="prop">
                        <div
                            :style="
                                prop.node.children && prop.node.children.length
                                    ? 'font-weight: bold; font-size: 15px'
                                    : ''
                            "
                        >
                            {{ prop.node.label }}
                        </div>
                    </template>
                </q-tree>
                <!-- button to select all/clear all options -->
                <q-btn
                    flat
                    color="primary"
                    :label="$t('Select All')"
                    @click="selectAll"
                />
                <q-btn
                    flat
                    color="primary"
                    :label="$t('Clear')"
                    @click="clearAll"
                />
                <q-inner-loading :showing="loading">
                    <q-spinner size="50px" color="primary" />
                </q-inner-loading>
            </div>
        </q-menu>
    </q-btn>
</template>

<script>
import { debounce } from 'quasar';

/**
 * Debounce delay for emitting active options to parent (in milliseconds).
 */
const EMIT_DEBOUNCE_DELAY = 250;

export default {
    name: 'FilterPanel',
    props: {
        /**
         * Name to be displayed for the panel.
         */
        label: {
            type: String,
            required: false
        },
        /**
         * Options in the panel.
         * Items must be an object and have the following format: { label: ..., value: ... }
         */
        options: {
            type: Array,
            required: true
        },
        /**
         * Option to be selected
         */
        selectedOptions: {
            type: [Set],
            required: true
        },
        /**
         * Icon to be displayed in the panel.
         */
        icon: {
            type: String,
            required: false
        },
        /**
         * Enable/disable search option.
         */
        searchOption: {
            type: Boolean,
            required: false
        },
        /**
         * Toggle drop-down button outline
         */
        outline: {
            type: Boolean,
            required: false,
            default: true
        },
        /**
         * Label alignment. defaults to center
         */
        alignLabel: {
            type: String,
            required: false,
            default: 'center'
        },
        color: {
            type: String,
            required: false,
            default: 'accent'
        },
        dropdownMenuLabel: {
            type: String,
            required: false
        },
        /**
         * Loading state
         */
        loading: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    watch: {
        /**
         * Watch for changes in the current selected options to enable disable tick.
         * This watcher handles user interaction with the tree component.
         * @param {string} value - The current selected option.
         */
        currentSelection(value) {
            if (value) {
                const index = this.activeOptions.indexOf(value);
                if (index !== -1) {
                    // Create new array reference to trigger watcher reliably (compared to splice mutation)
                    this.activeOptions = this.activeOptions.filter(
                        (_, i) => i !== index
                    );
                } else {
                    // Create new array reference to trigger watcher reliably
                    this.activeOptions = [...this.activeOptions, value];
                }
                // Reset selection after processing to prevent selection accumulation
                this.$nextTick(() => {
                    this.currentSelection = '';
                });
            }
            // Note: Emission happens via activeOptions watcher below
        },
        /**
         * Watch for changes from parent component's selectedOptions prop.
         * Only updates local state, does not emit back to prevent infinite loop.
         */
        selectedOptions: {
            handler(newValue) {
                // Skip if already updating from prop to prevent circular updates
                if (this.propUpdateCounter > 0) {
                    return;
                }

                const newArray = Array.from(newValue);

                // Deep equality check to prevent unnecessary updates
                if (newArray.length !== this.activeOptions.length) {
                    this.syncActiveOptionsFromProp(newArray);
                    return;
                }

                // Set-based comparison for better performance than sorting
                const newSet = new Set(newArray);
                const currentSet = new Set(this.activeOptions);

                // Check if any value in newSet is not in currentSet (order-independent)
                let isDifferent = false;
                for (const val of newSet) {
                    if (!currentSet.has(val)) {
                        isDifferent = true;
                        break;
                    }
                }

                if (isDifferent) {
                    this.syncActiveOptionsFromProp(newArray);
                }
            },
            deep: false
        },
        /**
         * Watch for changes in local activeOptions to emit to parent.
         * This is triggered by user interactions (tree clicks, select all, clear all).
         */
        activeOptions: {
            handler() {
                // Only emit if change originated from user interaction (not from prop update)
                if (this.propUpdateCounter === 0) {
                    this.emitActiveOptions();
                }
            },
            deep: false
        }
    },
    emits: {
        toggle: value => {
            return typeof value === 'string' || typeof value === 'object';
        }
    },
    created() {
        // Initialize from parent prop without triggering watchers
        this.propUpdateCounter++;
        this.activeOptions = Array.from(this.selectedOptions);

        // Reset counter after Vue processes the change
        this.$nextTick(() => {
            this.propUpdateCounter--;
        });
    },
    data() {
        return {
            activeOptions: [],
            showFilter: false,
            filter: '',
            filterRef: {},
            currentSelection: '',
            /**
             * Counter to prevent infinite loop between parent prop updates and local state changes.
             * Tracks the number of pending prop updates to handle rapid successive updates correctly.
             */
            propUpdateCounter: 0
        };
    },
    methods: {
        /**
         * Updates local activeOptions state from parent's selectedOptions prop.
         * Uses update counter to prevent circular updates between parent and child.
         * @param {string[]} newArray - The new array of selected values.
         */
        syncActiveOptionsFromProp(newArray) {
            this.propUpdateCounter++;
            this.activeOptions = newArray;
            this.$nextTick(() => {
                this.propUpdateCounter--;
            });
        },
        /**
         * Emit active options to parent component.
         * Debounced to prevent excessive updates during rapid user interactions.
         */
        emitActiveOptions: debounce(function () {
            const emittableOptions =
                this.activeOptions.length === 0
                    ? new Set()
                    : new Set(this.activeOptions);

            this.$emit('toggle', emittableOptions);
        }, EMIT_DEBOUNCE_DELAY),
        /**
         * Reset the filter (search)
         */
        resetFilter() {
            this.filter = '';
            this.filterRef = {};
        },
        /**
         * Select all options.
         */
        selectAll() {
            const allValuesSet = new Set();

            // Collect all leaf values from the tree using Set for O(1) lookups
            this.options.forEach(option => {
                if (option.children) {
                    option.children.forEach(child => {
                        allValuesSet.add(child.value);
                    });
                } else if (option.value) {
                    allValuesSet.add(option.value);
                }
            });

            // Update using immutable pattern
            this.activeOptions = Array.from(allValuesSet);
        },
        /**
         * Clear all options.
         */
        clearAll() {
            this.activeOptions = [];
        }
    },
    mounted() {
        this.$q.iconSet.arrow.dropdown = '';
    }
};
</script>

<style lang="scss" scoped></style>
